import React, { useMemo, useState } from "react";
import { withAuthenticationRequired } from "@auth0/auth0-react";
import { Container, Card, Table, Button, Modal, Form, Badge, Alert } from "react-bootstrap";
import toast from "react-hot-toast";
import PageTitle from "@/common/PageTitle/PageTitle";
import {
  useArchiveEmployeeDepartmentMutation,
  useCreateEmployeeDepartmentMutation,
  useListEmployeeDepartmentsQuery,
  useRestoreEmployeeDepartmentMutation,
  useUpdateEmployeeDepartmentMutation,
} from "@/features/employees/employeeDepartmentsApi";
import type { ApiEmployeeDepartment } from "@/features/employees/employeeDepartmentTypes";
import { useHasPlatformPower } from "@/contexts/SuperAdminIdentityContext";
import styles from "./employee-departments.module.scss";

type DepartmentFormState = {
  displayName: string;
  sortOrder: string;
};

const EMPTY_FORM: DepartmentFormState = {
  displayName: "",
  sortOrder: "",
};

function extractErrorMessage(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    "data" in error &&
    error.data &&
    typeof error.data === "object" &&
    "message" in error.data &&
    typeof (error as { data: { message: unknown } }).data.message === "string"
  ) {
    return (error as { data: { message: string } }).data.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return "Request failed";
}

function statusBadge(status: string) {
  if (status === "archived") {
    return <Badge bg="secondary">Archived</Badge>;
  }
  return <Badge bg="success">Active</Badge>;
}

function sortDepartments(departments: ApiEmployeeDepartment[]) {
  return [...departments].sort((a, b) => {
    const orderDiff = (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    if (orderDiff !== 0) return orderDiff;
    return a.displayName.localeCompare(b.displayName);
  });
}

const EmployeeDepartmentsPage: React.FC = () => {
  const canManageDepartments = useHasPlatformPower("manage-employee-departments");
  const { data, isLoading, isError, refetch } = useListEmployeeDepartmentsQuery();
  const [createDepartment, { isLoading: creating }] = useCreateEmployeeDepartmentMutation();
  const [updateDepartment, { isLoading: updating }] = useUpdateEmployeeDepartmentMutation();
  const [archiveDepartment, { isLoading: archiving }] = useArchiveEmployeeDepartmentMutation();
  const [restoreDepartment, { isLoading: restoring }] = useRestoreEmployeeDepartmentMutation();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showArchivedSection, setShowArchivedSection] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState<ApiEmployeeDepartment | null>(null);
  const [form, setForm] = useState<DepartmentFormState>(EMPTY_FORM);

  const activeDepartments = useMemo(
    () =>
      sortDepartments(
        (data?.departments ?? []).filter((department) => department.status === "active"),
      ),
    [data?.departments],
  );

  const archivedDepartments = useMemo(
    () =>
      sortDepartments(
        (data?.departments ?? []).filter((department) => department.status === "archived"),
      ),
    [data?.departments],
  );

  const openAddModal = () => {
    setForm(EMPTY_FORM);
    setShowAddModal(true);
  };

  const openEditModal = (department: ApiEmployeeDepartment) => {
    setSelectedDepartment(department);
    setForm({
      displayName: department.displayName,
      sortOrder: String(department.sortOrder ?? ""),
    });
    setShowEditModal(true);
  };

  const openArchiveModal = (department: ApiEmployeeDepartment) => {
    setSelectedDepartment(department);
    setShowArchiveModal(true);
  };

  const openRestoreModal = (department: ApiEmployeeDepartment) => {
    setSelectedDepartment(department);
    setShowRestoreModal(true);
  };

  const handleCreate = async () => {
    const displayName = form.displayName.trim();
    if (!displayName) {
      toast.error("Department name is required.");
      return;
    }
    try {
      await createDepartment({
        displayName,
        sortOrder: form.sortOrder.trim() ? Number(form.sortOrder) : undefined,
      }).unwrap();
      toast.success("Department created.");
      setShowAddModal(false);
      setForm(EMPTY_FORM);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleUpdate = async () => {
    if (!selectedDepartment) return;
    const displayName = form.displayName.trim();
    if (!displayName) {
      toast.error("Department name is required.");
      return;
    }
    try {
      await updateDepartment({
        departmentId: selectedDepartment.departmentId,
        displayName,
        sortOrder: form.sortOrder.trim() ? Number(form.sortOrder) : selectedDepartment.sortOrder,
      }).unwrap();
      toast.success("Department updated.");
      setShowEditModal(false);
      setSelectedDepartment(null);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleArchive = async () => {
    if (!selectedDepartment) return;
    try {
      await archiveDepartment(selectedDepartment.departmentId).unwrap();
      toast.success(`${selectedDepartment.displayName} archived.`);
      setShowArchiveModal(false);
      setSelectedDepartment(null);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  const handleRestore = async () => {
    if (!selectedDepartment) return;
    try {
      await restoreDepartment(selectedDepartment.departmentId).unwrap();
      toast.success(`${selectedDepartment.displayName} restored.`);
      setShowRestoreModal(false);
      setSelectedDepartment(null);
      setShowArchivedSection(false);
    } catch (error) {
      toast.error(extractErrorMessage(error));
    }
  };

  return (
    <Container fluid className="p-4">
      <Card className="shadow-sm">
        <Card.Body>
          <div className={styles.headerRow}>
            <PageTitle title="Employee Departments" />
            {canManageDepartments && (
              <Button variant="primary" onClick={openAddModal}>
                Add Department
              </Button>
            )}
          </div>

          <p className={styles.helpText}>
            Manage the department catalog used by employee records. Departments in use cannot be
            archived. Archived departments remain visible on historical employee records but are
            hidden from create/edit dropdowns.
          </p>

          {isError && (
            <Alert variant="danger">
              Failed to load departments.{" "}
              <Button variant="link" className="p-0 align-baseline" onClick={() => refetch()}>
                Retry
              </Button>
            </Alert>
          )}

          <Table responsive hover className={`mt-3 legacy-row-height-table ${styles.departmentsTable}`}>
            <thead>
              <tr>
                <th>Department ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Employees</th>
                <th>Sort</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={6}>Loading departments…</td>
                </tr>
              )}
              {!isLoading && activeDepartments.length === 0 && (
                <tr>
                  <td colSpan={6}>No active departments found.</td>
                </tr>
              )}
              {!isLoading &&
                activeDepartments.map((department) => (
                  <tr key={department.departmentId}>
                    <td>{department.departmentId}</td>
                    <td>{department.displayName}</td>
                    <td>{statusBadge(department.status)}</td>
                    <td>{department.employeeCount ?? 0}</td>
                    <td>{department.sortOrder}</td>
                    <td>
                      {canManageDepartments ? (
                        <div className={styles.actionButtons}>
                          <Button
                            variant="link"
                            className={styles.actionLink}
                            onClick={() => openEditModal(department)}
                            title="Edit department"
                            aria-label="Edit department"
                          >
                            <i className="bi bi-pencil" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="link"
                            className={styles.archiveLink}
                            onClick={() => openArchiveModal(department)}
                            title="Archive department"
                            aria-label="Archive department"
                          >
                            <i className="bi bi-archive" aria-hidden="true" />
                          </Button>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>

          {!isLoading && archivedDepartments.length > 0 && (
            <div className={styles.archivedSection}>
              <Button
                variant="link"
                className={styles.archivedToggle}
                onClick={() => setShowArchivedSection((open) => !open)}
                aria-expanded={showArchivedSection}
              >
                {showArchivedSection
                  ? `Hide archived departments (${archivedDepartments.length})`
                  : `Show archived departments (${archivedDepartments.length})`}
              </Button>

              {showArchivedSection && (
                <Table
                  responsive
                  hover
                  className={`legacy-row-height-table ${styles.departmentsTable}`}
                >
                  <thead>
                    <tr>
                      <th>Department ID</th>
                      <th>Name</th>
                      <th>Employees</th>
                      <th>Sort</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {archivedDepartments.map((department) => (
                      <tr key={department.departmentId}>
                        <td>{department.departmentId}</td>
                        <td>{department.displayName}</td>
                        <td>{department.employeeCount ?? 0}</td>
                        <td>{department.sortOrder}</td>
                        <td>
                          {canManageDepartments ? (
                            <Button
                              variant="link"
                              className={styles.restoreLink}
                              onClick={() => openRestoreModal(department)}
                              title="Restore department"
                              aria-label="Restore department"
                            >
                              <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
                            </Button>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Department</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Department Name</Form.Label>
            <Form.Control
              value={form.displayName}
              onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
              placeholder="e.g. Technology"
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Sort Order (optional)</Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={form.sortOrder}
              onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={creating} onClick={handleCreate}>
            Create Department
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Department</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDepartment && (
            <>
              <p className="text-muted mb-3">{selectedDepartment.departmentId}</p>
              <Form.Group className="mb-3">
                <Form.Label>Department Name</Form.Label>
                <Form.Control
                  value={form.displayName}
                  onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Sort Order</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={form.sortOrder}
                  onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={updating} onClick={handleUpdate}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Restore Department</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDepartment && (
            <Alert variant="info">
              Restore <strong>{selectedDepartment.displayName}</strong>? It will return to the
              active catalog and appear in employee create/edit dropdowns again.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRestoreModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" disabled={restoring} onClick={handleRestore}>
            Restore Department
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showArchiveModal} onHide={() => setShowArchiveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Archive Department</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDepartment && (
            <>
              <Alert variant="warning">
                Archive <strong>{selectedDepartment.displayName}</strong>? This removes it from
                employee create/edit dropdowns. Employees already assigned keep their historical
                department label. Departments in use cannot be archived.
              </Alert>
              <p className="mb-0">
                Employees currently assigned: <strong>{selectedDepartment.employeeCount}</strong>
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowArchiveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={archiving || (selectedDepartment?.employeeCount ?? 0) > 0}
            onClick={handleArchive}
          >
            Archive Department
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default withAuthenticationRequired(EmployeeDepartmentsPage);
